"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractProviderMessageContent = void 0;
exports.getResourceDtGenerationSchema = getResourceDtGenerationSchema;
exports.parseResourceDtGenerationResponse = parseResourceDtGenerationResponse;
exports.parseAiConfigResponse = parseAiConfigResponse;
const zod_1 = require("zod");
const prompt_registry_1 = require("./prompt-registry");
const dt_1 = require("./schemas/dt");
const extractProviderMessageContent = (response) => {
    if (!response || typeof response !== 'object' || Array.isArray(response))
        return response;
    const choices = response.choices;
    if (!Array.isArray(choices) || choices.length === 0)
        return response;
    const firstChoice = choices[0];
    if (!firstChoice || typeof firstChoice !== 'object' || Array.isArray(firstChoice))
        return response;
    const message = firstChoice.message;
    if (!message || typeof message !== 'object' || Array.isArray(message))
        return response;
    return message.content ?? response;
};
exports.extractProviderMessageContent = extractProviderMessageContent;
const parseJsonContent = (content) => {
    const providerContent = (0, exports.extractProviderMessageContent)(content);
    if (typeof providerContent !== 'string')
        return providerContent;
    return JSON.parse(providerContent);
};
function getResourceDtGenerationSchema(configType) {
    const dtSchema = (0, prompt_registry_1.getPromptSchema)(configType)?.zodSchema;
    const entitySheetSchema = (0, prompt_registry_1.getPromptSchema)('enrich-entity-sheet')?.zodSchema;
    if (!dtSchema)
        throw new Error(`No prompt schema registered for config type ${configType}`);
    if (!entitySheetSchema)
        throw new Error('No prompt schema registered for config type enrich-entity-sheet');
    // Keep the persisted config type as the prompt source, while validating the
    // complete DT contract used by ERP. The composed schema preserves the
    // existing root and aside field contracts instead of inventing a second DT.
    return zod_1.z.object({ dt: dt_1.resourceDtGenerationSchema, entitySheet: entitySheetSchema });
}
function parseResourceDtGenerationResponse(configType, content) {
    const parsed = parseJsonContent(content);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
        throw new Error('Invalid AI generation object');
    const raw = parsed;
    const dt = raw.dt ?? raw;
    const entitySheet = raw.entitySheet;
    const result = getResourceDtGenerationSchema(configType).parse({ dt, entitySheet });
    return result;
}
/**
 * Parses an AI response with the schema registered for the persisted config type.
 * The temporary { dt, entitySheet } envelope remains supported for ERP callers,
 * while the canonical shared config response stays at the root level.
 */
function parseAiConfigResponse(configType, content) {
    const schemaEntry = (0, prompt_registry_1.getPromptSchema)(configType);
    if (!schemaEntry)
        throw new Error(`No prompt schema registered for config type ${configType}`);
    const parsed = parseJsonContent(content);
    const directResult = schemaEntry.zodSchema.safeParse(parsed);
    if (directResult.success)
        return directResult.data;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && 'entitySheet' in parsed) {
        const entitySheet = parsed.entitySheet;
        const entitySheetResult = schemaEntry.zodSchema.safeParse(entitySheet);
        if (entitySheetResult.success)
            return entitySheetResult.data;
    }
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && 'dt' in parsed) {
        const envelope = parsed;
        const dt = envelope.dt && typeof envelope.dt === 'object' && !Array.isArray(envelope.dt) ? envelope.dt : {};
        const envelopeResult = schemaEntry.zodSchema.safeParse({
            ...dt,
            ...(envelope.entitySheet !== undefined ? { entitySheet: envelope.entitySheet } : {}),
        });
        if (envelopeResult.success)
            return envelopeResult.data;
    }
    throw directResult.error;
}
